import { Injectable, Inject, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';


declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class YoutubePlayerService {

  private player: any;
  private apiLoaded = false;
  private apiPromise?: Promise<void>;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private zone: NgZone) { }
  loadYouTubeAPI(): Promise<void> {
    // ✅ SSR: do nothing on server
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    // ✅ Reuse existing promise
    if (this.apiPromise) {
      return this.apiPromise;
    }

    this.apiPromise = new Promise((resolve, reject) => {
      // ✅ If already loaded
      if ((window as any).YT?.Player) {
        this.apiLoaded = true;
        resolve();
        return;
      }

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;

      tag.onerror = () => {
        console.error('Failed to load YouTube API');
        this.apiPromise = undefined; // allow retry
        reject();
      };

      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);

      (window as any).onYouTubeIframeAPIReady = () => {
        this.zone.run(() => {
          this.apiLoaded = true;
          resolve();
        });
      };
    });

    return this.apiPromise;
  }

  private setupResizeListener(): void {
    window.addEventListener('resize', () => {
      if (this.player && this.player.getIframe) {
        const iframe = this.player.getIframe();
        const container = iframe.parentElement;
        if (container) {
          const newWidth = container.offsetWidth;
          const newHeight = Math.round(newWidth * 0.5625);
          this.player.setSize(newWidth, newHeight);
        }
      }
    });
  }


  async playTrailer(videoId: string, elementId: string): Promise<void> {
    try {
      await this.loadYouTubeAPI();
      
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const container = document.getElementById(elementId);
      if (!container) return;

      const containerWidth = container.offsetWidth || 1200;
      const containerHeight = Math.round(containerWidth * 0.5625);

      this.player = new (window as any).YT.Player(elementId, {
        height: containerHeight.toString(),
        width: containerWidth.toString(),
        videoId,
        playerVars: {
          autoplay: 0,
          controls: 1,
          modestbranding: 1,
          rel: 0
        },
        events: {
          onReady: (event: any) => {
            event.target.playVideo();
            this.setupResizeListener();
          },
          onError: (event: any) => this.handleError(event)
        }
      });
    } catch (error) {
      console.error('Failed to play trailer:', error);
    }
  }

  destroyPlayer(): void {
      if(this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  private handleError(event: any): void {
    console.error('YouTube player error:', event.data);
  }
}